import React, { useEffect, useState } from 'react';
import { Button, Col, Image, Row, Stack } from 'react-bootstrap';
import { MdOutlineInfo } from 'react-icons/md';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import CustomInputPassword from '../../components/common/CustomInputPassword';
import Loader from '../../components/common/loader';
import AppTooltip from '../../components/common/tooltip';
import { PASSWORD_VALIDATION } from '../../constants/messages';
import storage from '../../helpers/storage';
import { setupPasswordAPI } from '../../services/app.users.service';
import { SetupPasswordSchema } from './validation';
import { redirectionRoutes } from '../../routes/redirectionRoutes';

/**
 * Form values type definition.
 */
type FormValues = {
    password: string;
    confirmPassword: string;
}

export default function ResetPassword() {
    const AppName = process.env.REACT_APP_NAME;

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState('');

    /**
     * Initial Form Values
     */
    const initialValues: FormValues = {
        password: '',
        confirmPassword: '',
    }

    /**
     * Hook to check if user is already login
     * OR
     * Has invalid URL. 
     */
    useEffect(() => {
        if (storage.getToken() != null) {
            return navigate('/');
        }
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
            setToken(tokenParam);
        } else {
            // invalid link when no token in the URL.
            navigate(redirectionRoutes.login.path);
        }
    }, [navigate, searchParams]);


    /**
     * Submit Method to call when user clicks on submit button
     * @async
     * @param {string} password
     * @param { any } actions
     * @returns {*}
     */
    const handleSubmit = async (values: Record<string, any>, actions: any) => {
        setLoading(true);
        actions.setSubmitting(true);
        const formData: any = { token, password: values.password }

        setupPasswordAPI(formData).then((response: any) => {
            setLoading(false);
            actions.setSubmitting(false);
            toast(response.message, { type: toast.TYPE.SUCCESS });
            navigate(redirectionRoutes.login.path, { replace: true });
        }).catch((error: any) => {
            setLoading(false);
            actions.setSubmitting(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
        })
    }

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100">
                <Col
                    md={6}
                >
                    <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                        <div className='user-select-none'>
                            <Image
                                fluid={true}
                                src={LOGO_WHITE}
                                alt={`${AppName} Logo`}
                                width={240}
                                height={80}
                            />
                        </div>
                        <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName}`}
                                width={500}
                                height={334}
                            />
                        </div>
                        <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>AI-Powered</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div>
                    </Stack>
                </Col>
                <Col md={6} className="col-md-6 d-flex flex-column p-3">
                    <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                        <div className='my-auto py-4'>
                            <h1 className="fw-semibold mb-1 fs-2">Setup Password</h1>
                            <p className="text-secondary mb-4 pb-1">Please enter new password</p>
                            <CommonFormikComponent
                                validationSchema={SetupPasswordSchema}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {(formikProps) => (
                                    <>
                                        <CustomInputPassword
                                            wrapperClassname="mb-3 pb-1"
                                            controlId="password"
                                            label={<Stack direction='horizontal' gap={1}>Password <AppTooltip title={PASSWORD_VALIDATION}><Button type="button" variant='link' className='p-0 border-0 lh-sm link-dark'><MdOutlineInfo /></Button></AppTooltip></Stack>}
                                            touched={formikProps.touched.password}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.password}
                                            value={formikProps.values.password}
                                        />
                                        <CustomInputPassword
                                            wrapperClassname="mb-4"
                                            controlId="confirmPassword"
                                            label={<Stack direction='horizontal' gap={1}>Confirm Password <AppTooltip title={PASSWORD_VALIDATION}><Button type="button" variant='link' className='p-0 border-0 lh-sm link-dark'><MdOutlineInfo /></Button></AppTooltip></Stack>}
                                            touched={formikProps.touched.confirmPassword}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.confirmPassword}
                                            value={formikProps.values.confirmPassword}
                                        />
                                        <Stack direction="horizontal" gap={3} className="justify-content-end">
                                            <Button className="w-100" variant="primary" type="submit" disabled={formikProps.isSubmitting || loading}>
                                                {loading ? 'Processing...' : 'Submit'}
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                            </CommonFormikComponent>
                        </div>
                        <div className='bg-dark bg-opacity-05 custom-min-height-40 fs-13 p-2 rounded text-center w-100 d-inline-flex align-items-center justify-content-center'>
                            <span className='pe-1'>Back to </span> <Link className="fw-semibold text-decoration-none" to={redirectionRoutes.login.path}>Login</Link>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    )
}
